<script setup lang="ts">
import { ref, computed } from "vue";
import { useTranslation } from "i18next-vue";
import { useInstallPrompt } from "@/composable/useInstallPrompt";
import DRBtn from "@/components/UI/DRBtn.vue";
import DRDialog from "@/components/UI/feedbacks/DRDialog.vue";
import PhosphorIcon from "@/components/UI/PhosphorIcon.vue";

const props = withDefaults(
  defineProps<{
    size?: "xs" | "sm" | "lg";
    variant?: "primary" | "outline" | "flat";
    block?: boolean;
    /** Libellé personnalisé (par défaut : "Installer l'application"). */
    label?: string;
  }>(),
  { size: "sm", variant: "primary", block: false, label: "" }
);

const { t } = useTranslation();

const buttonLabel = computed(() => props.label || t("install.button"));
const { canInstall, isIos, promptInstall } = useInstallPrompt();

const showIos = ref(false);

const onClick = async () => {
  if (canInstall.value) {
    await promptInstall(); // installation native Chromium
  } else if (isIos()) {
    showIos.value = true; // pas d'install programmatique sur iOS → instructions
  }
};
</script>

<template>
  <DRBtn
    :class="{ 'w-100': block }"
    icon="bi-download"
    :label="buttonLabel"
    :variant="variant"
    :size="size"
    @click="onClick"
  />

  <!-- Instructions iOS (pas d'API d'installation sur Safari) -->
  <DRDialog v-model="showIos" :title="t('install.iosTitle')" hide-footer>
    <ol class="install-ios">
      <li>
        <PhosphorIcon name="share-network" :size="20" weight="bold" />
        <span>{{ t("install.iosStep1") }}</span>
      </li>
      <li>
        <PhosphorIcon name="plus-square" :size="20" weight="bold" />
        <span>{{ t("install.iosStep2") }}</span>
      </li>
      <li>
        <span class="install-ios__num">3</span>
        <span>{{ t("install.iosStep3") }}</span>
      </li>
    </ol>
  </DRDialog>
</template>

<style lang="scss" scoped>
.install-ios {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 1rem;

  li {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    color: var(--bs-body-color);
  }

  li > svg,
  .install-ios__num {
    flex-shrink: 0;
    color: var(--bs-primary);
  }

  &__num {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    font-weight: 700;
  }
}
</style>
